import axios from '../../axios-config';
import { storageEmpresaId, storageEmpresaNombre, storageMonedaId } from '../../assets/shared/sessionData';
import * as actionTypes from './actionTypes';
import { enqueueSnackbar } from './snackbar';

// Impresión usa axios-config y no axios-dashboard: axios-dashboard deja pasar un request por vez
// y generar un PDF puede tardar varios segundos.
// Los importes de listados, detalle, circuito y valores vienen convertidos a la moneda elegida (idMoneda);
// el PDF queda en la moneda del comprobante.

// idSistema: circuito de Ver comprobantes, 'V' (ventas: Vnt_*, clientes) o 'C' (compras: Cmp_*, proveedores).

//#region load tipos de comprobante
export const loadTiposComprobanteImpresion = (idSistema = 'V', empresaId = storageEmpresaId()) => {
  return (dispatch) => {
    dispatch(loadTiposComprobanteImpresionStart());
    axios
      .get(`/impresion/TiposComprobante?idEmpresa=${empresaId}&idSistema=${idSistema}`)
      .then((response) => {
        dispatch(loadTiposComprobanteImpresionSuccess(response.data));
      })
      .catch((error) => {
        dispatch(loadTiposComprobanteImpresionFail(error));
      });
  };
};

const loadTiposComprobanteImpresionStart = () => {
  return {
    type: actionTypes.LOAD_TIPOS_COMPROBANTE_IMPRESION_START
  };
};

const loadTiposComprobanteImpresionSuccess = (tiposComprobante) => {
  return {
    type: actionTypes.LOAD_TIPOS_COMPROBANTE_IMPRESION_SUCCESS,
    tiposComprobante
  };
};

const loadTiposComprobanteImpresionFail = (error) => {
  return {
    type: actionTypes.LOAD_TIPOS_COMPROBANTE_IMPRESION_FAIL,
    error
  };
};
//#endregion

//#region load comprobantes
export const loadComprobantesImpresion = (filtros, empresaId = storageEmpresaId()) => {
  return (dispatch) => {
    dispatch(loadComprobantesImpresionStart());
    const params = new URLSearchParams({
      idEmpresa: empresaId,
      idSistema: filtros.idSistema || 'V',
      fechaDesde: filtros.fechaDesde,
      fechaHasta: filtros.fechaHasta,
      idMoneda: storageMonedaId() || '',
    });
    if (filtros.cliente) params.append('cliente', filtros.cliente);
    if (filtros.idComproba) params.append('idComproba', filtros.idComproba);
    if (filtros.sucursalId) params.append('sucursalId', filtros.sucursalId);
    if (filtros.nroExterno) params.append('nroExterno', filtros.nroExterno);

    axios
      .get(`/impresion/Comprobantes?${params.toString()}`)
      .then((response) => {
        dispatch(loadComprobantesImpresionSuccess(response.data));
      })
      .catch((error) => {
        dispatch(loadComprobantesImpresionFail(error));
        dispatch(
          enqueueSnackbar({
            message: `No se pudieron buscar los comprobantes${error.response && typeof error.response.data === 'string' ? `: ${error.response.data}` : ''}.`,
            options: {
              variant: 'error'
            }
          })
        );
      });
  };
};

const loadComprobantesImpresionStart = () => {
  return {
    type: actionTypes.LOAD_COMPROBANTES_IMPRESION_START
  };
};

const loadComprobantesImpresionSuccess = (comprobantes) => {
  return {
    type: actionTypes.LOAD_COMPROBANTES_IMPRESION_SUCCESS,
    comprobantes
  };
};

const loadComprobantesImpresionFail = (error) => {
  return {
    type: actionTypes.LOAD_COMPROBANTES_IMPRESION_FAIL,
    error
  };
};
//#endregion

//#region load pedidos
// Solo se aplica la respuesta de la última búsqueda, para que un refresco que vuelve tarde no pise una búsqueda nueva.
let ultimaBusquedaPedidos = 0;

/**
 * Notas de pedido de la sección Pedidos. Usa el mismo listado del store que Impresión, así las acciones
 * (PDF, mail, WhatsApp) actualizan la fila.
 * silencioso: refresco automático, sin spinner ni mensaje de error. Pide todos los estados, para saber el estado nuevo
 * de los que ya están, y actualiza el listado sin recargarlo (ver REFRESCAR_COMPROBANTES_IMPRESION).
 */
export const loadPedidosImpresion = (filtros, silencioso = false, empresaId = storageEmpresaId()) => {
  return (dispatch) => {
    const busqueda = ++ultimaBusquedaPedidos;
    if (!silencioso) dispatch(loadComprobantesImpresionStart());
    const params = new URLSearchParams({
      idEmpresa: empresaId,
      fechaDesde: filtros.fechaDesde,
      fechaHasta: filtros.fechaHasta,
      idMoneda: storageMonedaId() || '',
    });
    if (!silencioso) filtros.estados.forEach((estado) => params.append('estados', estado));

    axios
      .get(`/impresion/Pedidos?${params.toString()}`)
      .then((response) => {
        if (busqueda !== ultimaBusquedaPedidos) return;
        dispatch(
          silencioso
            ? { type: actionTypes.REFRESCAR_COMPROBANTES_IMPRESION, comprobantes: response.data, estados: filtros.estados }
            : loadComprobantesImpresionSuccess(response.data)
        );
      })
      .catch((error) => {
        if (silencioso || busqueda !== ultimaBusquedaPedidos) return;
        dispatch(loadComprobantesImpresionFail(error));
        dispatch(
          enqueueSnackbar({
            message: `No se pudieron buscar los pedidos${error.response && typeof error.response.data === 'string' ? `: ${error.response.data}` : ''}.`,
            options: {
              variant: 'error'
            }
          })
        );
      });
  };
};
//#endregion

export const clearImpresion = () => {
  // Descarta los pedidos en curso: si vuelven después de salir de la página no deben llenar el listado.
  ultimaBusquedaPedidos++;
  return {
    type: actionTypes.CLEAR_IMPRESION
  };
};

//#region pdf
/** Empresa y clave del comprobante, como los piden los endpoints de Impresión. */
const claveComprobante = (comprobante, empresaId) => ({
  idEmpresa: empresaId,
  idSucursal: comprobante.idSucursal,
  idSistema: comprobante.idSistema,
  idComproba: comprobante.idComproba,
  nroReferencia: comprobante.nroReferencia
});

/**
 * Pide el PDF del comprobante. El blob no se guarda en el store: lo usa el visor y se libera al cerrarlo.
 * success(blob, nombreArchivo); fail(mensaje) con el motivo que devuelve el servidor del cliente
 * (no soportado, sin CAE, etc.). registrar = false para la vista previa: no queda como descargado.
 */
export const loadPdfComprobanteImpresion = (comprobante, success, fail, registrar = true, empresaId = storageEmpresaId()) => {
  return (dispatch) => {
    const params = new URLSearchParams({ ...claveComprobante(comprobante, empresaId), registrar });
    axios
      .get(`/impresion/Pdf?${params.toString()}`, { responseType: 'blob' })
      .then((response) => {
        const ultimaAccion = leerUltimaAccion(response.headers['x-ultima-accion']);
        if (ultimaAccion) dispatch(actualizarUltimaAccionImpresion(comprobante, ultimaAccion));
        success && success(response.data, nombreArchivo(response.headers['content-disposition']));
      })
      .catch((error) => {
        leerMensajeError(error).then((mensaje) => {
          dispatch(
            enqueueSnackbar({
              message: `No se pudo generar el PDF: ${mensaje}`,
              options: {
                variant: 'error'
              }
            })
          );
          fail && fail(mensaje);
        });
      });
  };
};

const nombreArchivo = (contentDisposition) => {
  if (!contentDisposition) return 'comprobante.pdf';
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition);
  if (utf8) return decodeURIComponent(utf8[1]);
  const simple = /filename="?([^";]+)"?/i.exec(contentDisposition);
  return simple ? simple[1] : 'comprobante.pdf';
};

// Con responseType 'blob' el cuerpo del error también llega como Blob: hay que leerlo para ver { codigo, mensaje }.
const leerMensajeError = (error) => {
  const data = error.response && error.response.data;
  if (!data || typeof data.text !== 'function') {
    return Promise.resolve(error.message || 'error de conexión');
  }
  return data
    .text()
    .then((texto) => {
      try {
        const json = JSON.parse(texto);
        return json.mensaje || json.title || texto;
      } catch (e) {
        return texto || error.message;
      }
    })
    .catch(() => error.message);
};


// La API manda la descarga registrada en X-Ultima-Accion (JSON con encodeURIComponent).
const leerUltimaAccion = (header) => {
  if (!header) return null;
  try {
    return JSON.parse(decodeURIComponent(header));
  } catch (e) {
    return null;
  }
};
//#endregion

//#region detalle, circuito e historial
/**
 * GET de un endpoint de Impresión con la clave del comprobante, parametrosExtra (fijos del endpoint) y parametros
 * (de cada llamada; un array se manda repetido). No se guarda en el store. success(data); fail(mensaje).
 */
const loadDatosComprobante = (endpoint, descripcion, parametrosExtra = {}) => (comprobante, success, fail, parametros = {}, empresaId = storageEmpresaId()) => {
  return () => {
    const params = new URLSearchParams({ ...claveComprobante(comprobante, empresaId), idMoneda: storageMonedaId() || '', ...parametrosExtra });
    Object.keys(parametros).forEach((nombre) =>
      [].concat(parametros[nombre]).forEach((valor) => params.append(nombre, valor))
    );
    axios
      .get(`/impresion/${endpoint}?${params.toString()}`)
      .then((response) => success && success(response.data))
      .catch((error) => {
        const mensaje = error.response && typeof error.response.data === 'string' && error.response.data
          ? error.response.data
          : error.message || 'error de conexión';
        fail && fail(`No se pudo cargar ${descripcion}: ${mensaje}`);
      });
  };
};

/** Renglones del comprobante (con lo pendiente de descargar) y los comprobantes que lo descargaron. success({ renglones, descargas }) */
export const loadDetalleComprobanteImpresion = loadDatosComprobante('Detalle', 'el detalle');

/**
 * Sólo los renglones del comprobante, sin buscar quién lo descargó. Con parametros.facturas
 * ("idSucursal|idSistema|idComproba|nroReferencia") trae también sus saldos pendientes de cobro.
 * success({ renglones, descargas: [], saldosFacturas })
 */
export const loadRenglonesComprobanteImpresion = loadDatosComprobante('Detalle', 'los renglones', { conDescargas: false });

/**
 * Circuito del comprobante: los que lo descargaron y los que descargaron a esos, hasta el final, y los recibos
 * imputados a sus facturas, con lo cobrado. success({ clave, nodos, relaciones, importePago })
 */
export const loadCircuitoComprobanteImpresion = loadDatosComprobante('Circuito', 'el circuito');

/** Valores con que se pagó un recibo (con su detalle) y a qué comprobantes se imputó. success({ valores, imputaciones }) */
export const loadValoresReciboImpresion = loadDatosComprobante('ValoresRecibo', 'los valores del recibo');

/** Todas las descargas y envíos del comprobante, de la más nueva a la más vieja. success(acciones) */
export const loadHistorialComprobanteImpresion = loadDatosComprobante('Historial', 'el historial');

/** Registra la descarga del PDF que se bajó desde la vista previa (que no la registra) y actualiza la fila. */
export const registrarDescargaImpresion = (comprobante, empresaId = storageEmpresaId()) => {
  return (dispatch) => {
    axios
      .post('/impresion/RegistrarDescarga', claveComprobante(comprobante, empresaId))
      .then((response) => dispatch(actualizarUltimaAccionImpresion(comprobante, response.data)))
      .catch(() => {
        // El PDF ya se bajó; si no se pudo registrar sólo queda sin actualizar el estado.
      });
  };
};
//#endregion

//#region mail
/** Mails del cliente (del proveedor si idSistema es 'C') en el Eiffel, para precargar el destinatario. success(mails) */
export const loadMailDestinatarioImpresion = (idCliente, success, fail, idSistema = 'V', empresaId = storageEmpresaId()) => {
  return () => {
    const params = new URLSearchParams({ idEmpresa: empresaId, idCliente: idCliente ? idCliente.trim() : '', idSistema: idSistema || 'V' });
    axios
      .get(`/impresion/MailDestinatario?${params.toString()}`)
      .then((response) => success && success(response.data || []))
      .catch(() => fail && fail());
  };
};

/**
 * Envía el PDF por mail desde la cuenta del usuario del Eiffel asociado. Si sale bien actualiza el
 * Estado de la fila con la acción que devuelve la API.
 */
export const enviarMailComprobanteImpresion = (comprobante, mail, success, fail, empresaId = storageEmpresaId()) => {
  return (dispatch) => {
    axios
      .post('/impresion/EnviarMail', {
        ...claveComprobante(comprobante, empresaId),
        destinatarios: mail.destinatarios,
        asunto: mail.asunto,
        mensaje: mail.mensaje
      })
      .then((response) => {
        dispatch(actualizarUltimaAccionImpresion(comprobante, response.data));
        dispatch(
          enqueueSnackbar({
            message: 'Comprobante enviado por mail',
            options: {
              key: new Date().getTime() + Math.random(),
              variant: 'success'
            }
          })
        );
        success && success();
      })
      .catch((error) => {
        const data = error.response && error.response.data;
        const mensaje = (data && (data.mensaje || (typeof data === 'string' && data))) || error.message || 'error de conexión';
        dispatch(
          enqueueSnackbar({
            message: `No se pudo enviar el mail: ${mensaje}`,
            options: {
              variant: 'error'
            }
          })
        );
        fail && fail(mensaje);
      });
  };
};
//#endregion

//#region remitente
/**
 * Nombre con el que se firman los envíos por mail y WhatsApp (Clientes.Nombre del Dashboard).
 * success(nombre) con el nombre, o el de la empresa de la sesión si la API no devuelve uno.
 */
export const loadRemitenteImpresion = (success, fail, empresaId = storageEmpresaId()) => {
  return () => {
    const params = new URLSearchParams({ idEmpresa: empresaId });
    axios
      .get(`/impresion/Remitente?${params.toString()}`)
      .then((response) => {
        const nombre = response.data && response.data.nombre ? response.data.nombre.trim() : '';
        success && success(nombre || (storageEmpresaNombre() || '').trim());
      })
      .catch(() => fail && fail());
  };
};
//#endregion

//#region whatsapp
/**
 * Si la empresa tiene WhatsApp configurado y el teléfono del cliente (del proveedor si idSistema es 'C') en el
 * Eiffel, para precargar el destinatario. success({ configurado, telefono })
 */
export const loadWhatsappDestinatarioImpresion = (idCliente, success, fail, idSistema = 'V', empresaId = storageEmpresaId()) => {
  return () => {
    const params = new URLSearchParams({ idEmpresa: empresaId, idCliente: idCliente ? idCliente.trim() : '', idSistema: idSistema || 'V' });
    axios
      .get(`/impresion/WhatsappDestinatario?${params.toString()}`)
      .then((response) => success && success(response.data || { configurado: false }))
      .catch(() => fail && fail());
  };
};

/**
 * Envía el PDF por WhatsApp con la cuenta de WhatsApp Business de la empresa. Si sale bien actualiza
 * el Estado de la fila con la acción que devuelve la API.
 */
export const enviarWhatsappComprobanteImpresion = (comprobante, whatsapp, success, fail, empresaId = storageEmpresaId()) => {
  return (dispatch) => {
    axios
      .post('/impresion/EnviarWhatsapp', {
        ...claveComprobante(comprobante, empresaId),
        telefono: whatsapp.telefono,
        nombreCliente: whatsapp.nombreCliente,
        comprobante: whatsapp.comprobante
      })
      .then((response) => {
        dispatch(actualizarUltimaAccionImpresion(comprobante, response.data));
        dispatch(
          enqueueSnackbar({
            message: 'Comprobante enviado por WhatsApp',
            options: {
              key: new Date().getTime() + Math.random(),
              variant: 'success'
            }
          })
        );
        success && success();
      })
      .catch((error) => {
        const data = error.response && error.response.data;
        const mensaje = (data && (data.mensaje || (typeof data === 'string' && data))) || error.message || 'error de conexión';
        dispatch(
          enqueueSnackbar({
            message: `No se pudo enviar el WhatsApp: ${mensaje}`,
            options: {
              variant: 'error'
            }
          })
        );
        fail && fail(mensaje);
      });
  };
};
//#endregion

const actualizarUltimaAccionImpresion = (comprobante, ultimaAccion) => {
  return {
    type: actionTypes.ACTUALIZAR_ULTIMA_ACCION_IMPRESION,
    comprobante,
    ultimaAccion
  };
};
